/**
 * Pure helpers for a real date+time chart axis (#228 Phase 3). Kept free of React
 * / DOM so they are unit-testable (L1): given a [from, to) span they pick a
 * human-friendly tick granularity, enumerate the tick timestamps, and label each
 * one adaptively (time-of-day for sub-day spans, date for multi-day spans).
 */

/** A single axis tick: its timestamp and its display label. */
export interface AxisTick { ts: number; label: string; }

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

// Candidate tick granularities (ms), ascending. The resolver picks the smallest
// rung that keeps the tick count under TARGET_TICKS so labels never collide.
const TICK_LADDER = [
  MINUTE, 5 * MINUTE, 15 * MINUTE, 30 * MINUTE,
  HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR,
  DAY, 7 * DAY, 30 * DAY
] as const;

const TARGET_TICKS = 8;

/** Pick a tick interval (ms) so the span yields at most ~TARGET_TICKS ticks. */
export function pickTickInterval(spanMs: number): number {
  for (const rung of TICK_LADDER) {
    if (spanMs / rung <= TARGET_TICKS) return rung;
  }
  return TICK_LADDER[TICK_LADDER.length - 1]!;
}

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Label one tick. Sub-day spans show HH:MM; spans of a day or more show the date
 * (MM-DD), and a tick that lands on local midnight always shows its date so the
 * day boundary is legible even inside a sub-day window.
 */
export function tickLabel(ts: number, intervalMs: number): string {
  const d = new Date(ts);
  const atMidnight = d.getHours() === 0 && d.getMinutes() === 0;
  if (intervalMs >= DAY || atMidnight) return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Enumerate axis ticks across [from, to], snapped to round wall-clock boundaries
 * of the chosen interval. The first tick is the first boundary at or after
 * `from`; ticks step by the interval up to `to`. Returns at most TARGET_TICKS+2.
 */
export function axisTicks(from: number, to: number): AxisTick[] {
  if (!(to > from)) return [];
  const interval = pickTickInterval(to - from);
  // Snap up to the next interval boundary at/after `from`. Use a date-aware floor
  // for day-scale intervals so ticks land on local midnight, not a UTC multiple.
  const first = Math.ceil(from / interval) * interval;
  const ticks: AxisTick[] = [];
  for (let ts = first; ts <= to; ts += interval) {
    ticks.push({ ts, label: tickLabel(ts, interval) });
  }
  return ticks;
}
